import React, { createContext, useEffect, useRef, useState } from "react";
import { dynamicHeaderGenerator } from "../utils/dynamicHeaderGenerator";
import { fetchDataFromAPI } from "../api/dataGridApiService";
import { APIResponseType } from "../types";
import {
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridSelectionModel,
  GridSortModel,
} from "@mui/x-data-grid-pro";
import { useQuery, useQueryClient } from "@tanstack/react-query";

const GRID_ROW_ID_FIELD = "__gridRowId";

// Define the context type
export type DataGridContextType = {
  data: any[];
  setData: React.Dispatch<React.SetStateAction<any[]>>;
  headers: GridColDef[];
  setHeaders: React.Dispatch<React.SetStateAction<GridColDef[]>>;
  backendHeaders: any[];
  setBackendHeaders: React.Dispatch<React.SetStateAction<any[]>>;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  totalPages: number;
  setTotalPages: React.Dispatch<React.SetStateAction<number>>;
  totalDocuments: number;
  setTotalDocuments: React.Dispatch<React.SetStateAction<number>>;
  cache: Record<string, any>;
  setCache: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  error: string;
  setError: React.Dispatch<React.SetStateAction<string>>;
  columnSortModel: GridSortModel;
  setColumnSortModel: React.Dispatch<React.SetStateAction<GridSortModel>>;
  columnVisibilityModel: GridColumnVisibilityModel;
  setColumnVisibilityModel: React.Dispatch<
    React.SetStateAction<GridColumnVisibilityModel>
  >;
  columnFilterModel: GridFilterModel;
  setColumnFilterModel: React.Dispatch<React.SetStateAction<GridFilterModel>>;
  rowSelectionModel: GridSelectionModel;
  setRowSelectionModel: React.Dispatch<
    React.SetStateAction<GridSelectionModel>
  >;
  filterableFields: string[];
  fetchData: (
    endpoint: string,
    params?: Record<string, any>,
    page?: number,
    pageSize?: number,
    filters?: GridFilterModel,
    reset?: boolean,
    sortModel?: GridSortModel
  ) => Promise<void>;
  manualUpdateData: (rowId: string, updates: Record<string, any>) => void;
  forceUpdate: number;
};

// Create the context with default values
export const DataGridContext = createContext<DataGridContextType>({
  data: [],
  setData: () => {},
  headers: [],
  setHeaders: () => {},
  backendHeaders: [],
  setBackendHeaders: () => {},
  currentPage: 1,
  setCurrentPage: () => {},
  totalDocuments: 0,
  setTotalDocuments: () => {},
  cache: {},
  setCache: () => {},
  loading: false,
  setLoading: () => {},
  error: "",
  setError: () => {},
  columnSortModel: [],
  setColumnSortModel: () => {},
  columnVisibilityModel: {},
  setColumnVisibilityModel: () => {},
  columnFilterModel: { items: [] },
  setColumnFilterModel: () => {},
  rowSelectionModel: [],
  setRowSelectionModel: () => {},
  filterableFields: [],
  fetchData: async () => {},
  totalPages: 0,
  setTotalPages: () => {},
  manualUpdateData: () => {},
  forceUpdate: 0,
});

// Define the provider component
const DataGridProvider: React.FC<{ 
  children: React.ReactNode; 
  renderCellMapping: any[]; 
  onCellClick: (params: any, clickedElement?: string, extraData?: any) => void; 
  sortModel: GridSortModel; 
  visibleColumns: any[]; 
  filterableFields: string[]; 
  externalManualUpdate?: (rowId: string, updates: Record<string, any>) => void;
  /** Initial filter model (e.g. offers: is_active, end_date). Sent in filters param. */
  initialFilterModel?: GridFilterModel;
}> = ({
  children, renderCellMapping, onCellClick, sortModel, visibleColumns, filterableFields, externalManualUpdate, initialFilterModel,
}) => {

  const isMounted = useRef(true);

  // on unmount, mark it false
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);
  const [data, setData] = useState<any[]>([]);
  const [headers, setHeaders] = useState<GridColDef[]>([]);
  const [backendHeaders, setBackendHeaders] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [cache, setCache] = useState<Record<string, any>>({});
  const [columnSortModel, setColumnSortModel] = useState<GridSortModel>(sortModel);
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});
  const [columnFilterModel, setColumnFilterModel] = useState<GridFilterModel>(
    initialFilterModel && initialFilterModel.items?.length ? initialFilterModel : { items: [] }
  );
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridSelectionModel>([]);
  const [forceUpdate] = useState(0);
  const rowSequenceRef = useRef(0);

  const queryClient = useQueryClient();

  // Function to manually update a specific row in the data
  const manualUpdateData = (rowId: string, updates: Record<string, any>) => {
    if (!isMounted.current) return;

    // If external manualUpdate function is provided, use it
    if (externalManualUpdate) {
      externalManualUpdate(rowId, updates);
      return;
    }

    // Update the internal data state
    setData(prevData => {
      const updatedData = [...prevData];
      const rowIndex = updatedData.findIndex(row => row._id === rowId);

      if (rowIndex >= 0) {
        updatedData[rowIndex] = {
          ...updatedData[rowIndex],
          ...updates,
          last_update: new Date().toISOString()
        };
      }

      return updatedData;
    });

    // Also update React Query cache to ensure DataGrid shows the changes
    const queries = queryClient.getQueriesData({
      queryKey: ['GET'],
      exact: false
    });

    queries.forEach(([queryKey, queryData]) => {
      if (queryData && typeof queryData === 'object' && 'body' in queryData) {
        const apiResponse = queryData as any;
        if (apiResponse.body && apiResponse.body.data && Array.isArray(apiResponse.body.data)) {
          const dataIndex = apiResponse.body.data.findIndex((row: any) => row._id === rowId);
          if (dataIndex >= 0) {
            const updatedQueryData = {
              ...apiResponse,
              body: {
                ...apiResponse.body,
                data: apiResponse.body.data.map((row: any, index: number) => 
                  index === dataIndex 
                    ? { ...row, ...updates, last_update: new Date().toISOString() }
                    : row
                )
              }
            };
            queryClient.setQueryData(queryKey, updatedQueryData);
          }
        }
      }
    });

    // Avoid forcing DataGrid remounts here; remount resets scroll position.
  };

  // Function to set column headers dynamically
  const setColumnHeaders = (
    data: any[],
    headers: any[],
    isOpportunity: boolean
  ) => {
    try {
      
      // Find prospects field in backend headers
      const prospectsHeader = headers.find(h => h.db_field === 'prospects' || h.input_file_field === 'prospects');
      
      if (data.length > 0 && renderCellMapping.length) {
        const viewWidth = window.innerWidth - 200;
        const mappedHeaders: GridColDef[] =
          dynamicHeaderGenerator(headers, data, isOpportunity, onCellClick, viewWidth, renderCellMapping, visibleColumns, filterableFields) || [];
        setHeaders(mappedHeaders);
      }
    } catch (error) {
      console.error("Error setting column headers:", error);
    }
  };

  // Function to fetch data from the API using TanStack Query
  const attachGridRowIds = (rows: any[], reset: boolean) => {
    if (!Array.isArray(rows)) {
      return [];
    }

    if (reset) {
      rowSequenceRef.current = 0;
    }

    return rows.map((row: any) => {
      rowSequenceRef.current += 1;
      return {
        ...row,
        [GRID_ROW_ID_FIELD]: `${rowSequenceRef.current}`,
      };
    });
  };

  const fetchData = async (
    endpoint: string,
    params: Record<string, any> = {},
    page: number = 1,
    pageSize: number = 20,
    filters: GridFilterModel = { items: [] },
    reset = false,
    sortModel: GridSortModel = []
  ) => {
    if (!isMounted.current) return;
    setLoading(true);
    setError("");

    try {

      // Create a unique query key based on all parameters
      const queryKey = [
        'GET',
        endpoint, 
        params, 
        page,
        JSON.stringify(filters),
        JSON.stringify(sortModel)
      ];

      // Use TanStack Query to fetch the data
      const response = await queryClient.ensureQueryData<APIResponseType>({
        queryKey,
        queryFn: async () => {
          return fetchDataFromAPI(endpoint, params, page, pageSize, JSON.stringify(filters), JSON.stringify(sortModel));
        }
      });

      // Update state with the fetched data
      setTotalDocuments(response.body.meta.totalDocuments);
      setCurrentPage(page);
      setTotalPages(response.body.meta.totalPages);
      
      // Store backend headers for filter panel
      setBackendHeaders(response.body.headers);

      // Merge synthetic headers for visibleColumns not returned by API (e.g. action column)
      const apiHeaders = response.body.headers || [];
      const apiFieldSet = new Set(apiHeaders.map((h: any) => h.db_field || h.input_file_field));
      const syntheticHeaders = (visibleColumns || []).filter(
        (field: string) => !apiFieldSet.has(field)
      ).map((field: string) => ({
        db_field: field,
        display_name: field.charAt(0).toUpperCase() + field.slice(1).replace(/_/g, ' '),
        field_data_type: 'string',
        field_required: false,
        input_file_field: field,
        null_allowed: true,
      }));
      const mergedHeaders = [...apiHeaders, ...syntheticHeaders];

      if (headers.length === 0) {
        setColumnHeaders(
          response.body.data,
          mergedHeaders,
          response.body.meta.isOpportunity
        );
      }
      
      const nextRows = attachGridRowIds(response.body.data, reset);

      if (reset) {
        setData(nextRows);
      } else {
        setData(old => old.concat(nextRows));
      }
    } catch (error) {
      if (!isMounted.current) return;
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError("An unknown error occurred");
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  // Provide the context value
  const contextValue: DataGridContextType = {
    data,
    setData,
    headers,
    setHeaders,
    backendHeaders,
    setBackendHeaders,
    currentPage,
    setCurrentPage,
    totalPages,
    setTotalPages,
    totalDocuments,
    setTotalDocuments,
    cache,
    setCache,
    loading,
    setLoading,
    error,
    setError,
    columnSortModel,
    setColumnSortModel,
    columnVisibilityModel,
    setColumnVisibilityModel,
    columnFilterModel,
    setColumnFilterModel,
    rowSelectionModel,
    setRowSelectionModel,
    filterableFields,
    fetchData,
    manualUpdateData: manualUpdateData,
    forceUpdate: forceUpdate,
  };

  return (
    <DataGridContext.Provider value={contextValue}>
      {children}
    </DataGridContext.Provider>
  );
};

export default DataGridProvider;